import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const AboutSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  const stats = [
    { number: "150+", label: "Projects Completed", icon: "CheckCircle" },
    { number: "50+", label: "Happy Clients", icon: "Users" },
    { number: "24/7", label: "Support Available", icon: "Clock" },
    { number: "99.9%", label: "Uptime Guarantee", icon: "Shield" }
  ];

  const values = [
    {
      icon: "Lightbulb",
      title: "Innovation",
      description: "Modern solutions that push technological boundaries"
    },
    {
      icon: "Target",
      title: "Precision",
      description: "Accurate delivery focused on your specific business needs"
    },
    {
      icon: "Heart",
      title: "Partnership",
      description: "Long-term relationships built on trust and mutual success"
    },
    {
      icon: "Zap",
      title: "Agility",
      description: "Rapid adaptation to changing market demands and technologies"
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 bg-background relative overflow-hidden">
      {/* Parallax Background Elements */}
      <motion.div 
        style={{ y, opacity }}
        className="absolute inset-0 opacity-10"
      >
        <div className="absolute top-20 left-10 w-32 h-32 border border-accent rounded-full" />
        <div className="absolute top-40 right-20 w-24 h-24 border border-accent rounded-full" />
        <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-accent rounded-full" />
      </motion.div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              About <span className="text-accent">DARAJA.LABS</span>
            </h2>
            
            <p className="text-lg text-text-secondary mb-6 leading-relaxed">
              DARAJA.LABS is more than just an IT services company – we are digital transformation architects. 
              Our name "DARAJA" means "bridge" in Swahili, reflecting our core mission of connecting businesses 
              to their digital future.
            </p>
            
            <p className="text-lg text-text-secondary mb-8 leading-relaxed">
              Founded with a vision to democratize modern technology, we specialize in creating seamless 
              bridges between traditional business operations and modern digital ecosystems. Our team of expert 
              developers, designers, and strategists work collaboratively to deliver solutions that don't just meet today's needs, but anticipate tomorrow's challenges.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-colors duration-200"
                >
                  Learn More About Us
                </motion.button>
              </Link>
              
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border border-accent text-accent px-6 py-3 rounded-lg font-semibold hover:bg-accent hover:text-accent-foreground transition-colors duration-200"
                >
                  Get In Touch
                </motion.button>
              </Link>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-2xl">
              <Image
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="DARAJA.LABS team collaboration"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
            </div>
            
            {/* Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="absolute -bottom-8 -left-8 bg-card/90 backdrop-blur-sm border border-border rounded-xl p-6 shadow-xl"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-accent" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">5+ Years</div>
                  <div className="text-sm text-text-secondary">Industry Experience</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
        >
          {stats?.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <div className="w-16 h-16 bg-accent/20 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-accent/30 transition-colors duration-300">
                <Icon name={stat?.icon} size={28} className="text-accent" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">{stat?.number}</div>
              <div className="text-text-secondary">{stat?.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
            Our Core <span className="text-accent">Values</span>
          </h3>
          <p className="text-text-secondary max-w-2xl mx-auto">
            The principles that guide every project and partnership we build
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values?.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              className="text-center group cursor-pointer"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-accent/20 to-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-accent/30 group-hover:to-accent/20 transition-all duration-300">
                <Icon name={value?.icon} size={32} className="text-accent" />
              </div>
              <h4 className="text-xl font-semibold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                {value?.title}
              </h4>
              <p className="text-text-secondary leading-relaxed">
                {value?.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;